/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { useRef } from "react";
import { NotificationApi } from "client";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import { useCallApi } from "hooks/CallApiHook";
import { Const } from "const";
import { useInterval } from "react-use";

/**
 * Notification update check.
 * @return {NewEventsNewNotification} check result.
 * @throws {TimeoutError} timeout occurred.
 * @throws {HttpError} Http response exist but status code is not success.
 * @throws {FetchError} Can't connect error.
 */
export const useNotificationCheck = () => {
  const prevLoadDate = useRef(new Date());
  const [notificationApi, cancelNotificationApi] = useApiWithTimeout(
    NotificationApi,
    Const.operationTimeout.getNotificationList
  );
  const { retry, loading, value } = useCallApi(
    async () => {
      const notifications = await notificationApi.getNotificationUpdate({
        startDatetime: prevLoadDate.current,
      });
      prevLoadDate.current = notifications.lastCheckDatetime;
      return notifications.newNotification;
    },
    cancelNotificationApi,
    [cancelNotificationApi, notificationApi]
  );

  useInterval(() => {
    if (!loading) {
      retry();
    }
  }, Const.eventInterval);

  return value;
};
